import React, { useEffect, useRef, useState } from 'react';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Point, LineString, Polygon, MultiPoint } from 'ol/geom';
import Feature from 'ol/Feature';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import { Modal } from '@stormwater-software/lib-ui';
import { useMap } from './useMap';
import projectApi from '../../utils/api/project';

const ESRI_COLOR = '#ff6b00';
const META_KEYS = ['modify_date', 'modify_user'];

const STYLE = new Style({
  fill: new Fill({ color: 'rgba(255,107,0,0.18)' }),
  stroke: new Stroke({ color: ESRI_COLOR, width: 2 }),
  image: new CircleStyle({
    radius: 4,
    fill: new Fill({ color: ESRI_COLOR }),
    stroke: new Stroke({ color: '#fff', width: 1 }),
  }),
});

// Inverse of bilinearPixelToGeo for an axis-aligned georeference (top corners
// share lat, side corners share lng — what GISConfigure captures).
function geoToPixel(lng, lat, imgW, imgH, pu) {
  const minLng = Math.min(pu.geo_top_left_lng, pu.geo_bottom_left_lng);
  const maxLng = Math.max(pu.geo_top_right_lng, pu.geo_bottom_right_lng);
  const minLat = Math.min(pu.geo_bottom_left_lat, pu.geo_bottom_right_lat);
  const maxLat = Math.max(pu.geo_top_left_lat, pu.geo_top_right_lat);
  const u = (lng - minLng) / (maxLng - minLng);
  const v = (lat - minLat) / (maxLat - minLat); // OL y-up
  return [u * imgW, v * imgH];
}

function projectRing(ring, imgW, imgH, pu) {
  return ring.map(([lng, lat]) => geoToPixel(lng, lat, imgW, imgH, pu));
}

function buildGeometry(row, imgW, imgH, pu) {
  const c = row.coordinates;
  switch (row.geometry_type) {
    case 'Point':
      return new Point(geoToPixel(c[0], c[1], imgW, imgH, pu));
    case 'MultiPoint':
      return new MultiPoint(projectRing(c, imgW, imgH, pu));
    case 'LineString':
      return new LineString(projectRing(c, imgW, imgH, pu));
    case 'Polygon':
      return new Polygon(c.map((ring) => projectRing(ring, imgW, imgH, pu)));
    default:
      return null;
  }
}

// Splits a feature's properties into [meta, rest] — meta has modify_date/modify_user
function partitionAttributes(attrs) {
  const meta = {};
  const rest = {};
  Object.entries(attrs || {}).forEach(([k, v]) => {
    if (META_KEYS.includes(k.toLowerCase())) meta[k] = v;
    else rest[k] = v;
  });
  return { meta, rest };
}

function AttributesTable({ rows }) {
  const entries = Object.entries(rows);
  if (entries.length === 0) return <p style={{ color: '#888' }}>No data</p>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <tbody>
        {entries.map(([key, value]) => {
          const displayValue =
            key.toLowerCase() === 'modify_date' && typeof value === 'number'
              ? (() => {
                  const d = new Date(value);
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  return `${yyyy}/${mm}/${dd}`;
                })()
              : value == null
              ? '—'
              : String(value);
          return (
            <tr key={key} style={{ borderBottom: '1px solid #eee' }}>
              <td
                style={{
                  padding: '4px 8px',
                  fontWeight: 600,
                  width: '40%',
                  verticalAlign: 'top',
                }}
              >
                {key}
              </td>
              <td style={{ padding: '4px 8px', wordBreak: 'break-word' }}>
                {displayValue}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Renders esri_positionables (lat/lng-native) as an OL vector layer over the
 * site map, projecting each feature to image-pixel space using the upload's
 * 4-corner georeference. Click a feature to open an attribute modal.
 */
export default function EsriFeaturesLayer({
  projectId,
  projectUpload,
  refreshTick,
  hidden,
}) {
  const olMap = useMap();
  const layerRef = useRef(null);
  const [selectedAttrs, setSelectedAttrs] = useState(null);

  useEffect(() => {
    if (!olMap || !projectId || !projectUpload?.id) return;
    if (!projectUpload.gis_calibrated) return;
    if (projectUpload.geo_top_left_lat == null) return;

    const extent = olMap.getView().getProjection().getExtent();
    const imgW = extent[2];
    const imgH = extent[3];
    if (!imgW || !imgH) return;

    let cancelled = false;
    const source = new VectorSource({ wrapX: false });
    const layer = new VectorLayer({ source, style: STYLE, zIndex: 50 });
    olMap.addLayer(layer);
    layerRef.current = layer;

    // Click handler — only fires for features on this ESRI layer
    const onClick = (event) => {
      olMap.forEachFeatureAtPixel(
        event.pixel,
        (feature) => {
          const attrs = feature.get('esriAttributes');
          if (attrs) setSelectedAttrs(attrs);
          return true; // stop iterating after first hit
        },
        { layerFilter: (l) => l === layer },
      );
    };
    olMap.on('singleclick', onClick);

    // Pointer cursor when hovering an ESRI feature
    const onPointerMove = (event) => {
      if (event.dragging) return;
      const hit = olMap.hasFeatureAtPixel(event.pixel, {
        layerFilter: (l) => l === layer,
      });
      olMap.getViewport().style.cursor = hit ? 'pointer' : '';
    };
    olMap.on('pointermove', onPointerMove);

    projectApi.esriPositionables
      .list(projectId, projectUpload.id)
      .then((rows) => {
        if (cancelled) return;
        const features = (rows || [])
          .map((row) => {
            const geom = buildGeometry(row, imgW, imgH, projectUpload);
            if (!geom) return null;
            const f = new Feature({ geometry: geom });
            f.setProperties({ esriAttributes: row.attributes });
            f.setId(`esri-${row.id}`);
            return f;
          })
          .filter(Boolean);
        source.addFeatures(features);
      })
      .catch(() => {
        // Silent: the Refresh button surfaces errors for the user-driven path.
      });

    return () => {
      cancelled = true;
      olMap.un('singleclick', onClick);
      olMap.un('pointermove', onPointerMove);
      olMap.getViewport().style.cursor = '';
      olMap.removeLayer(layer);
      layerRef.current = null;
    };
  }, [
    olMap,
    projectId,
    projectUpload?.id,
    projectUpload?.gis_calibrated,
    refreshTick,
  ]);

  // Toggle visibility without rebuilding the layer
  useEffect(() => {
    if (layerRef.current) layerRef.current.setVisible(!hidden);
  }, [hidden]);

  if (!selectedAttrs) return null;

  const { meta, rest } = partitionAttributes(selectedAttrs);

  return (
    <Modal
      show
      title='ESRI Feature'
      handleClose={() => setSelectedAttrs(null)}
      handleSubmit={() => setSelectedAttrs(null)}
      submitBtnText='Close'
      cancelBtn={false}
    >
      <div
        style={{
          display: 'flex',
          gap: 16,
          padding: 12,
          maxHeight: '60vh',
          overflow: 'auto',
        }}
      >
        <div style={{ flex: 2, minWidth: 0 }}>
          <h4 style={{ marginTop: 0 }}>Attributes</h4>
          <AttributesTable rows={rest} />
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            borderLeft: '1px solid #eee',
            paddingLeft: 16,
          }}
        >
          <h4 style={{ marginTop: 0 }}>Last Modified</h4>
          <AttributesTable rows={meta} />
        </div>
      </div>
    </Modal>
  );
}
